import { Link } from "react-router-dom";
import { ArrowRight, Phone, ShieldCheck, ClipboardCheck, Hammer, Trees, Mountain, Waves, Route, Axe } from "lucide-react";
import { SERVICES, SITE } from "@/lib/siteData";

const ICONS = { Trees, Axe, Mountain, Waves, Route, Hammer };

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative isolate overflow-hidden bg-[#16302B] text-white">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_forestry-mulch-pro/artifacts/7s7rivse_images.jfif"
            alt="CAT 265 track loader with forestry mulcher in pine forest"
            className="h-full w-full object-cover opacity-70"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#16302B]/95 via-[#16302B]/70 to-transparent" />
        </div>

        <div className="relative mx-auto max-w-7xl px-6 pt-24 pb-24 md:px-12 md:pt-36 md:pb-32">
          <div>
            <p data-testid="home-hero-eyebrow" className="eyebrow reveal text-[#D95D39]">
              Forestry Mulching · Earthworks · Demolition
            </p>
            <h1
              data-testid="home-hero-title"
              className="reveal reveal-delay-1 mt-5 font-display text-5xl font-black leading-[0.92] tracking-tighter sm:text-6xl lg:text-7xl"
            >
              Reclaim your land with <span className="text-[#D95D39]">Mulberry Land Services.</span>
            </h1>
            <p
              data-testid="home-hero-subtitle"
              className="reveal reveal-delay-2 mt-7 max-w-xl text-base leading-relaxed text-white/85 md:text-lg"
            >
              Family-run forestry mulching and land services for landowners across the region.
              From overgrown lots to build-ready pads, we bring the right equipment to
              every job — and leave it cleaner than we found it.
            </p>

            <div className="reveal reveal-delay-3 mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/contact"
                data-testid="home-cta-quote"
                className="inline-flex items-center gap-2 bg-[#D95D39] px-7 py-4 text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-[#B94B2C]"
              >
                Get a Free Quote <ArrowRight size={16} strokeWidth={2.5} />
              </Link>
              <a
                href={SITE.phoneHref}
                data-testid="home-cta-call"
                className="inline-flex items-center gap-2 border-2 border-white/80 px-7 py-4 text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-white hover:text-[#16302B]"
              >
                <Phone size={16} strokeWidth={2.5} /> {SITE.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* VALUE PROPS BAR */}
      <section className="border-y border-[#D1CCC5] bg-[#E8E2DA]">
        <div className="mx-auto grid max-w-7xl grid-cols-1 divide-y divide-[#16302B]/15 px-6 md:grid-cols-2 md:divide-x md:divide-y-0 md:px-12">
          <ValueProp icon={ShieldCheck} title="Licensed & Insured" desc="Full liability and workers' comp on every job." />
          <ValueProp icon={ClipboardCheck} title="Free on-site estimates" desc="We walk your property and write a fixed-price quote — no cost, no pressure." />
        </div>
      </section>

      {/* SERVICES */}
      <section data-testid="home-services-section" className="bg-[#F9F8F6] py-20 md:py-32">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
            <p className="eyebrow">What we do</p>
            <Link
              to="/services"
              data-testid="home-services-view-all"
              className="inline-flex items-center gap-2 border-2 border-[#16302B] px-6 py-3 text-sm font-bold uppercase tracking-wider text-[#16302B] transition-colors hover:bg-[#16302B] hover:text-white"
            >
              View all services <ArrowRight size={14} strokeWidth={2.5} />
            </Link>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, idx) => {
              const Icon = ICONS[s.icon] || Trees;
              return (
                <Link
                  to="/services"
                  key={s.slug}
                  data-testid={`home-service-card-${s.slug}`}
                  className="group relative flex flex-col overflow-hidden border border-[#D1CCC5] bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)]"
                >
                  <div className="aspect-[4/3] w-full overflow-hidden bg-[#E8E2DA]">
                    <img
                      src={s.image}
                      alt={s.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading={idx > 2 ? "lazy" : "eager"}
                    />
                  </div>
                  <div className="flex flex-1 flex-col gap-3 p-7">
                    <div className="flex items-center gap-3">
                      <span className="grid h-10 w-10 place-items-center border border-[#16302B] text-[#16302B] transition-colors group-hover:bg-[#D95D39] group-hover:border-[#D95D39] group-hover:text-white">
                        <Icon size={18} strokeWidth={2} />
                      </span>
                      <h3 className="font-display text-xl font-bold tracking-tight text-[#16302B]">{s.title}</h3>
                    </div>
                    <p className="text-sm leading-relaxed text-[#5C5C5C]">{s.short}</p>
                    <span className="mt-auto inline-flex items-center gap-2 pt-2 text-xs font-bold uppercase tracking-[0.18em] text-[#D95D39]">
                      Learn more <ArrowRight size={12} strokeWidth={2.5} />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-[#16302B] py-20 text-white md:py-32">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <p className="eyebrow text-[#D95D39]">How it works</p>
          <h2 className="mt-3 max-w-3xl font-display text-3xl font-black tracking-tight sm:text-4xl lg:text-5xl">
            From the first call to the final walkthrough.
          </h2>

          <div className="mt-14 grid gap-px bg-white/10 md:grid-cols-4">
            {[
              { n: "01", t: "Walk the property", d: "Free on-site visit so we understand the terrain, access, and your goals." },
              { n: "02", t: "Fixed-price quote", d: "Written estimate within 48 hours — itemized, with timeline and scope." },
              { n: "03", t: "We get to work", d: "Right machine, right operator, daily progress updates." },
              { n: "04", t: "Walk-through & close", d: "Final inspection together — only paid when you're happy." },
            ].map((step) => (
              <div key={step.n} className="bg-[#16302B] p-8 md:p-10">
                <span className="font-display text-5xl font-black text-[#D95D39]">{step.n}</span>
                <h3 className="mt-5 font-display text-xl font-bold tracking-tight">{step.t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-white/75">{step.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#E8E2DA] py-20 md:py-32">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-10 px-6 md:flex-row md:items-end md:px-12">
          <div>
            <p className="eyebrow">Free, no-obligation</p>
            <h2 className="mt-3 max-w-2xl font-display text-3xl font-black leading-[0.95] tracking-tight text-[#16302B] sm:text-4xl lg:text-5xl">
              Tell us about your land — we'll send a quote in 48 hours.
            </h2>
          </div>
          <Link
            to="/contact"
            data-testid="home-bottom-cta"
            className="inline-flex items-center gap-2 bg-[#D95D39] px-7 py-4 text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-[#B94B2C]"
          >
            Request a Quote <ArrowRight size={16} strokeWidth={2.5} />
          </Link>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value, small }) {
  return (
    <div className="bg-[#16302B] px-6 py-7 md:py-8">
      <div className={`font-display font-black tracking-tight text-white ${small ? "text-xl" : "text-3xl md:text-4xl"}`}>
        {value}
      </div>
      <p className="mt-2 text-[10px] font-bold uppercase tracking-[0.22em] text-white/65">{label}</p>
    </div>
  );
}

function ValueProp({ icon: Icon, title, desc }) {
  return (
    <div className="flex items-start gap-4 px-6 py-7 md:px-10 md:py-10">
      <span className="grid h-11 w-11 shrink-0 place-items-center bg-[#16302B] text-[#D95D39]">
        <Icon size={20} strokeWidth={2} />
      </span>
      <div>
        <h3 className="font-display text-lg font-bold tracking-tight text-[#16302B]">{title}</h3>
        <p className="mt-1 text-sm text-[#5C5C5C]">{desc}</p>
      </div>
    </div>
  );
}
